'use client';

import { useCallback, useState, RefObject, createRef, memo } from 'react';
import Icon from '@/components/shared/Icons';
import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import ImageComponent from '@/components/ImageComponent';
import { type ImageProps } from 'next/image';
import useAnalytics from '@/hooks/useAnalytics';
import useMap from '@/hooks/useMap';
import { getVenueUrl } from '@/utils/venueDetailsHelper';
import type { SearchParams } from '@/models/SearchParams';
import type { Locale } from '@/i18n';
import { VenueProfileFactParams } from '@/components/card/VenueCard';
import { getSimpleClickFact } from '@/analytics/helpers/cventAnalytics/getSimpleClickFact';
import {
  CSN_FACT_SOURCE_VENUE_CARD,
  VENUE_CARD_CLICK_TYPE,
  VENUE_IMAGE_CAROUSEL_CLICK_ENTITY
} from '@/utils/analyticsClientHelper';
import type { SimpleClickFact } from '@/analytics/models/cventAnalytics/SimpleClickFact';
import getListingClickFact from '@/analytics/helpers/siteCatalyst/getListingClickFact';
import useSpinner from '@/hooks/useSpinner';
import { getAdClickFact } from '@/analytics/helpers/cventAnalytics/getAdClickFact';
import { AdClickFact } from '@/analytics/models/cventAnalytics/AdClickFact';
import { queryStringConstants } from '@/constants/queryConstants';
import { useSearchParams } from 'next/navigation';
import { VenueTag } from '@/models/VenueTag';
import { Shimmer, toBase64 } from '../skeletons/ImageSkeleton';

interface Props {
  venue: CsnSearchVenue;
  venueIndex: number;
  searchResultRank: number;
  searchTerm: string;
  priority: boolean;
  initialImages: string[];
  carouselStrings: {
    previousImage: string;
    nextImage: string;
    imageAltText: string;
    tag3D: string;
    tagFloorPlans: string;
    tagHasVideos: string;
  };
  getVenueProfileImages: (venueId: string) => Promise<{ id?: string; src: string }[]>;
  adType?: string;
  locale: Locale;
  searchParams: SearchParams;
  cventGuestDomain: string;
  basePath: string;
  isLoggedIn: boolean;
  accountId: string;
}

function Carousel({
  venue,
  venueIndex,
  searchResultRank,
  searchTerm,
  initialImages,
  carouselStrings,
  adType,
  locale,
  searchParams,
  cventGuestDomain,
  basePath,
  priority = false,
  getVenueProfileImages,
  isLoggedIn,
  accountId
}: Props) {
  const [images, setImages] = useState(initialImages);
  const [count, setCount] = useState(images.length);
  const [currentImage, setCurrentImage] = useState(0);
  const [profileImagesFetched, setProfileImagesFetched] = useState(false);

  const showNextImage: boolean = images.length > 1;
  const { mapExpanded } = useMap();
  const { setShowSpinner } = useSpinner();
  const analytics = useAnalytics();
  const params = useSearchParams();

  // tag on carousel
  const venueTags = venue.tags || [];
  const tagsToCheck = [VenueTag.Enum.HAS_3D_DIAGRAMS, VenueTag.Enum.SOCIAL_TABLES_ENABLED, VenueTag.Enum.HAS_VIDEO];
  const [venueHas3D, venueHasFloorPlans, venueHasVideos] = tagsToCheck.map(tag =>
    venueTags.some(venueTag => venueTag === tag)
  );
  const showCarouselTag: boolean = venueHas3D || venueHasFloorPlans || venueHasVideos;
  let tagLabel;
  if (showCarouselTag) {
    if (venueHas3D && venueHasFloorPlans) {
      tagLabel = `${carouselStrings.tag3D} | ${carouselStrings.tagFloorPlans}`;
    } else if (venueHas3D) {
      tagLabel = carouselStrings.tag3D;
    } else if (venueHasFloorPlans) {
      tagLabel = carouselStrings.tagFloorPlans;
    }
    if (venueHasVideos) {
      if (tagLabel) {
        tagLabel += ` | ${carouselStrings.tagHasVideos}`;
      } else {
        tagLabel = carouselStrings.tagHasVideos;
      }
    }
  }

  const handleClick = useCallback(
    async (e, imageRank: number) => {
      setShowSpinner(true);
      e.preventDefault();
      e.stopPropagation();

      // if ad -> trigger ad fact
      if (venue.listingCode) {
        analytics.reportSiteCatalystFact(
          getListingClickFact(isLoggedIn, accountId, venue.name, venue.listingCode, venue.code)
        );
        const adClickFact: AdClickFact = getAdClickFact(
          venue,
          venueIndex,
          mapExpanded,
          searchTerm,
          params?.get(queryStringConstants.RFP_ID),
          CSN_FACT_SOURCE_VENUE_CARD
        );
        await analytics.reportFactImmediately<AdClickFact>(adClickFact);
      }

      const venueProfileFactParams: VenueProfileFactParams = {
        adId: venue.listingCode,
        adType,
        adPos: venue.listingCode ? venueIndex + 1 : undefined,
        searchResultsRank: searchResultRank,
        searchTerm,
        imageRank: imageRank !== 0 ? imageRank : -1
      };
      const venueUrl = getVenueUrl(venue, locale, cventGuestDomain, basePath, searchParams, venueProfileFactParams);

      // open in new tab
      if (e.metaKey) {
        setShowSpinner(false);
        window.open(venueUrl);
      } else {
        window.open(venueUrl, '_self');
      }
    },
    [
      setShowSpinner,
      venue,
      adType,
      venueIndex,
      searchResultRank,
      searchTerm,
      locale,
      cventGuestDomain,
      basePath,
      searchParams,
      analytics,
      isLoggedIn,
      accountId,
      mapExpanded,
      params
    ]
  );

  const triggerSimpleClickFact = useCallback(() => {
    const simpleClickFact = getSimpleClickFact(
      VENUE_IMAGE_CAROUSEL_CLICK_ENTITY,
      VENUE_CARD_CLICK_TYPE,
      searchResultRank,
      venue.id,
      mapExpanded,
      searchTerm,
      venue.diamondLevel
    );
    analytics.reportFact<SimpleClickFact>(simpleClickFact);
  }, [analytics, mapExpanded, venue, searchResultRank, searchTerm]);

  const refs = images.reduce((acc: Array<RefObject<HTMLImageElement>>, val, i) => {
    acc[i] = createRef();
    return acc;
  }, [] as Array<RefObject<HTMLImageElement>>);

  const scrollToImage = useCallback(
    (i: number) => {
      setCurrentImage(i);
      refs[i]?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    },
    [refs]
  );

  const nextImage = useCallback(
    async (e): Promise<void> => {
      e.stopPropagation();
      e.preventDefault();
      triggerSimpleClickFact();
      if (currentImage >= count - 1) {
        scrollToImage(0);
      } else {
        scrollToImage(currentImage + 1);
      }

      // on first click, fetch more if available
      if (currentImage === 0 && !profileImagesFetched) {
        const venueImages = await getVenueProfileImages(venue.id);
        const venueImagesSrc = venueImages.slice(1).map(venueImage => venueImage.src);
        setImages(arr => [...arr, ...venueImagesSrc]);
        setCount(venueImagesSrc.length + images.length);
        setProfileImagesFetched(true);
      }
    },
    [
      triggerSimpleClickFact,
      currentImage,
      count,
      profileImagesFetched,
      scrollToImage,
      getVenueProfileImages,
      venue.id,
      images.length
    ]
  );

  const previousImage = useCallback(
    (e): void => {
      e.stopPropagation();
      e.preventDefault();
      triggerSimpleClickFact();
      if (currentImage === 0) {
        scrollToImage(count - 1);
      } else {
        scrollToImage(currentImage - 1);
      }
    },
    [count, currentImage, scrollToImage, triggerSimpleClickFact]
  );

  const sliderControl = useCallback(
    (isLeft: boolean) => (
      <button
        type="button"
        onClick={isLeft ? previousImage : nextImage}
        className={`text-2xl text-white absolute flex h-6 w-6 items-center justify-center rounded-full bg-neutral-10 group-hover:visible sm:invisible ${
          isLeft ? 'left-3 z-10' : 'right-3'
        }`}
        style={{ top: '45%' }}
        aria-label={isLeft ? carouselStrings.previousImage : carouselStrings.nextImage}
      >
        <Icon
          id={isLeft ? 'chevron-left' : 'chevron-right'}
          data-cvent-id={`venue-${isLeft ? 'previous' : 'next'}-image`}
          height={16}
          width={16}
          title={`Arrow ${isLeft ? 'left' : 'right'}`}
        />
      </button>
    ),
    [carouselStrings.nextImage, carouselStrings.previousImage, nextImage, previousImage]
  );

  return (
    <div className="group relative flex aspect-video w-full sm:h-full sm:max-h-[176px]" data-cvent-id="image-carousel">
      {showCarouselTag && (
        <div
          className="absolute left-1/2 top-[90%] z-10 grid h-auto w-max -translate-x-1/2 -translate-y-1/2 items-center
        overflow-hidden rounded-full bg-background px-2 py-0.5 text-b-xs/snug font-normal text-neutral-90 opacity-80"
          data-cvent-id="media-info"
        >
          <span>
            {venueHas3D && <Icon id="three-dimensional" className="float-left h-4 w-4 pr-1" fill="text-neutral-90" />}
            {tagLabel}
          </span>
        </div>
      )}
      <div className="inline-flex h-full w-full snap-x overflow-hidden">
        {currentImage > 0 && sliderControl(true)}
        {images.map((img, i) => {
          const imageProps: ImageProps = {
            priority: priority && i === 0,
            fill: true,
            sizes: '(max-width: 640px) 100vw, 25vw',
            style: { objectFit: 'cover', width: '100%' },
            src: img,
            alt: carouselStrings.imageAltText,
            placeholder: `data:image/svg+xml;base64,${toBase64(Shimmer(700, 475))}`
          };
          // don't want wrapper div to be accessible to screen reader
          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div
              onClick={e => handleClick(e, i)}
              className="relative h-full w-full flex-shrink-0"
              key={img}
              ref={refs[i]}
            >
              <ImageComponent imageProps={imageProps} />
            </div>
          );
        })}
        {showNextImage && sliderControl(false)}
      </div>
    </div>
  );
}

export default memo(Carousel);
